import { useCalculusFilters } from '@retail/calculus/context';
import { filterConditions } from '@retail/calculus/utils';
import { Stack } from '@mui/material';
import { UniversalFormProvider } from '@shared/components';
import { followPriceSuggestionSchema } from '@retail/price-suggestion/components';
import { useAppTFunction } from '@retail/app/i18n';
import { CalculusFilters } from './Filters';
import {
  calculusActionPortalId,
  ErrorFallback,
  FilterChipContainer,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
} from '@retail/components';
import { useDisclosure } from '@shared/hooks';
import { CalculusArea, ConditionsNew } from '@retail/calculus/types';
import { useMemo } from 'react';
import { HierarchyLevels } from '@retail/products/types';
import { ConditionCount } from '@retail/calculus/components';
import { RoundingConditionsContainer } from './Rounding';
import { useFetchConditions } from '@retail/calculus/data-access';
import {
  useIsFeatureEnabled,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { SalesPriceConditionsContainer } from './SalesPriceConditions';
import { brandLogo } from '@retail/utils';
import { SupplierBase } from '@retail/suppliers/types';
import { PurchaseConditionsContainer } from './PurchaseConditions';
import { FreightConditions } from './FreightConditions';
import { MarkupConditionContainer } from './MarkupConditions/MarkupConditionContainer';
import { PriceSuggestionConditions } from './PriceSuggestionConditions';
import { CustomerMarkupContainer } from './CustomerMarkupConditions/CustomerMarkupContainer';
import { AllConditionsContainer } from './AllConditions';
import { calculusFilters } from '@retail/products/utils';

interface Props {
  calculusArea: CalculusArea;
  selectedSupplier?: SupplierBase;
  isLoadingSuppliers: boolean;
}

export function CalculusContainer({ calculusArea, selectedSupplier, isLoadingSuppliers }: Props) {
  const t = useAppTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const salesPriceConditionsIsEnabled = useIsFeatureEnabled('sales-price-condition');

  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);

  const { productGroups, discountGroups, filters } = useCalculusFilters({
    supplierNumber: selectedSupplier?.supplierNumber,
    conditionType:
      calculusArea === 'ALL_CONDITIONS'
        ? undefined
        : calculusArea !== 'SALES_PRICES'
        ? calculusArea
        : 'MARKUP',
  });
  const { selectedBrand } = useUserBrands();

  const {
    data: conditionsDTO = [],
    isFetching,
    error,
  } = useFetchConditions({
    assortmentId,
    supplierNumber: selectedSupplier?.supplierNumber,
    productGroupNumber: filters.productGroupNumber ?? undefined,
    discountGroupCode: filters.discountGroupCode ?? undefined,
    conditionType: calculusArea === 'ALL_CONDITIONS' ? undefined : calculusArea,
    conditionState: filters.conditionState,
    assortmentType: filters.assortmentType,
    enabled: !isLoadingSuppliers,
  });
  const conditions = useMemo(
    () =>
      conditionsDTO.map((cond) =>
        ConditionsNew.fromDto(cond, {
          name: selectedBrand.name,
          id: selectedBrand.brandId,
          imgUrl: brandLogo(selectedBrand.brandId).logo,
        })
      ),
    [conditionsDTO, selectedBrand.brandId, selectedBrand.name]
  );

  const filteredConditions = useMemo(
    () =>
      filterConditions(conditions, {
        hierarchyLevel: filters.hierarchyLevel,
        itemSearchQuery: filters.itemSearchQuery,
      }),
    [conditions, filters.hierarchyLevel, filters.itemSearchQuery]
  );

  const isLoadingContent = useMemo(
    () => isLoadingSuppliers || isFetching,
    [isFetching, isLoadingSuppliers]
  );

  const availableHierarchyLevels: HierarchyLevels[] | undefined = useMemo(
    () => Array.from(new Set(conditionsDTO.map((cond) => cond.bindingType))),
    [conditionsDTO]
  );

  const availablePackageTypes = useMemo(
    () =>
      Array.from(new Set(conditionsDTO.map((cond) => cond.bindingTargetItem?.packageType))).filter(
        (type) => !!type
      ) ?? [],
    [conditionsDTO]
  );

  const priceSuggestionSchema = useMemo(() => followPriceSuggestionSchema(t), [t]);
  return (
    <Stack flexGrow={1} overflow="auto">
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'flex-end' }}>
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Stack id={calculusActionPortalId} direction="row" gap={1} minHeight={40} />

          <ToggleFiltersButton
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            buttonHeight={50}
            sx={{ mb: -1.1 }}
          />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack flexGrow={1} flexDirection="row">
        <Stack flexGrow={1} pt={1} justifyContent={error ? 'center' : undefined}>
          {error ? (
            <ErrorFallback message={error.payload.message ?? error.payload.detail ?? error.text} />
          ) : (
            <>
              <Stack flexDirection="row" justifyContent="space-between" px={2} height={32}>
                <FilterChipContainer
                  allowedKeys={calculusFilters}
                  filterOptions={{ productGroups, discountGroups, suppliers: [] }}
                />

                <ConditionCount totalCount={filteredConditions.length} />
              </Stack>
              {calculusArea === 'PURCHASE' && (
                <PurchaseConditionsContainer
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                  supplierNumber={selectedSupplier?.supplierNumber}
                />
              )}

              {calculusArea === 'FREIGHT' && (
                <FreightConditions
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                  supplierNumber={selectedSupplier?.supplierNumber}
                />
              )}
              {calculusArea === 'MARKUP' && !salesPriceConditionsIsEnabled && (
                <MarkupConditionContainer
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                  selectedSupplier={selectedSupplier}
                />
              )}
              {calculusArea === 'PRICE_SUGGESTION' && !salesPriceConditionsIsEnabled && (
                <UniversalFormProvider
                  schema={priceSuggestionSchema}
                  defaultValues={{
                    ...priceSuggestionSchema.getDefault(),
                    level: selectedSupplier ? '' : 'assortment',
                  }}
                >
                  <PriceSuggestionConditions
                    conditions={filteredConditions}
                    isFetching={isLoadingContent}
                    supplierNumber={selectedSupplier?.supplierNumber}
                    assortmentId={assortmentId}
                  />
                </UniversalFormProvider>
              )}

              {calculusArea === 'SALES_PRICES' && (
                <SalesPriceConditionsContainer
                  assortmentId={assortmentId}
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                  selectedSupplier={selectedSupplier}
                />
              )}

              {calculusArea === 'CUSTOMER_MARKUP' && (
                <CustomerMarkupContainer
                  supplierNumber={selectedSupplier?.supplierNumber}
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                />
              )}
              {calculusArea === 'ROUNDING' && (
                <RoundingConditionsContainer
                  supplier={selectedSupplier}
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                />
              )}
              {calculusArea === 'ALL_CONDITIONS' && (
                <AllConditionsContainer
                  conditions={filteredConditions}
                  isFetching={isLoadingContent}
                  selectedSupplier={selectedSupplier}
                  conditionState={filters.conditionState}
                />
              )}
            </>
          )}
        </Stack>
        <CalculusFilters
          productGroups={productGroups}
          discountGroups={discountGroups}
          conditionState={filters.conditionState}
          assortmentType={filters.assortmentType}
          productGroupNumber={filters.productGroupNumber}
          discountGroupCode={filters.discountGroupCode}
          itemSearchQuery={filters.itemSearchQuery}
          selectedHierarchyLevel={filters.hierarchyLevel}
          availableHierarchyLevels={!isLoadingSuppliers ? availableHierarchyLevels : undefined}
          availablePackageTypes={!isLoadingSuppliers ? availablePackageTypes : undefined}
          supplierNumber={selectedSupplier?.supplierNumber}
          isLoadingContent={isLoadingContent}
          showFilters={showFilters}
        />
      </Stack>
    </Stack>
  );
}
